import { lazy, Suspense, useCallback } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import Profile from "../../pages/Settings/Profile";
import { ACCOUNT_TYPES } from "../../utils";
import { GlobalLoader } from "../Common/Loader";
import TerraConnect from "../../pages/Settings/terra-connect";

const Login = lazy(() => import("../../pages/Login/Login"));
const AppLayout = lazy(() => import("../Layout/AppLayout"));
const PrivateRoute = lazy(() => import("../Common/PrivateRoute"));
const VerifyAccount = lazy(() => import("../../pages/VerifyAccount"));
const ForgotPassword = lazy(() => import("../../pages/ForgotPassword"));
const ResetPassword = lazy(() => import("../../pages/ResetPassword"));
const Registration = lazy(() =>
  import("../../pages/Registration/Registration")
);
const Settings = lazy(() => import("../../pages/Settings"));
const VerifyingSocialLogin = lazy(() =>
  import("../../pages/VerifyingSocialLogin")
);
const TypeOfAccount = lazy(() => import("../../pages/TypeOfAccount"));
const Approval = lazy(() => import("../../pages/Approval"));
const UserApproval = lazy(() => import("../../pages/Approval/UserApproval"));
const Analytics = lazy(() => import("../../pages/Approval/Analytics"));
const SocialMediaInformation = lazy(() =>
  import("../../pages/SocialMediaInformation")
);
const AmbassadorProgram = lazy(() => import("../../pages/AmbassadorProgram"));
const AnalyticsPlatform = lazy(() => import("../../pages/AnalyticsPlatform"));

const Health = lazy(() => import("../../pages/Health"));
const Steps = lazy(() => import("../../pages/Health/Steps"));
const MarketPlace = lazy(() => import("../../pages/Marketplace"));
const MindfulnessExercise = lazy(() =>
  import("../../pages/Health/MindfulnessExercise")
);
const MindfulnessDetails = lazy(() =>
  import("../../pages/Health/MindfulnessExercise/MindfulnessDetails")
);

const LongevityPointTransaction = lazy(() =>
  import("../../pages/LongevityPointTransaction")
);

const MyLongevity = lazy(() => import("../../pages/MyLongevity"));

const AppRoutes = () => {
  const getPrivateRoute = useCallback(
    (children) => (
      <AppLayout>
        <PrivateRoute>{children}</PrivateRoute>
      </AppLayout>
    ),
    []
  );

  const getPublicRoute = useCallback(
    (children) => <AppLayout>{children}</AppLayout>,
    []
  );

  return (
    <Suspense fallback={<GlobalLoader />}>
      <Routes>
        <Route path={"/callback"} element={<VerifyingSocialLogin />} />
        <Route
          path="/registration/:referral_code"
          element={getPublicRoute(<Registration />)}
        />
        <Route
          path="/registration"
          element={getPublicRoute(<Registration />)}
        />
        <Route
          path="/verify-account/:email"
          element={getPublicRoute(<VerifyAccount />)}
        />
        <Route
          path="/reset-password/:uid"
          element={getPublicRoute(<ResetPassword />)}
        />
        <Route path="/login" element={getPublicRoute(<Login />)} />
        <Route
          path="/forgot-password"
          element={getPublicRoute(<ForgotPassword />)}
        />
        <Route path="/settings" element={getPrivateRoute(<Settings />)}>
          <Route path="profile" element={<Profile />} />
        </Route>
        <Route path="/admin" element={getPrivateRoute(<Approval />)}>
          <Route
            path="ambassador"
            element={<UserApproval type={ACCOUNT_TYPES.AMBASSADOR} />}
          />
          <Route
            path="corporate-club-member"
            element={
              <UserApproval type={ACCOUNT_TYPES.CORPORATE_CLUB_MEMBER} />
            }
          />
          <Route path="analytics" element={<Analytics />} />
        </Route>
        <Route
          path="/type-of-account"
          element={getPrivateRoute(<TypeOfAccount />)}
        />
        <Route
          path="/marketplace"
          element={getPrivateRoute(<MarketPlace />)}
        />
        <Route
          path="/social-media-information"
          element={getPrivateRoute(<SocialMediaInformation />)}
        />
        <Route
          path="/ambassador-program"
          element={getPrivateRoute(<AmbassadorProgram />)}
        />
        <Route
          path="/analytics-platform"
          element={getPrivateRoute(<AnalyticsPlatform />)}
        />
        <Route path="/health/steps" element={getPrivateRoute(<Steps />)} />{" "}
        <Route
          path="/health/terra_connect"
          element={getPrivateRoute(<TerraConnect />)}
        />
        <Route
          path="/health/mindfulness-exercise"
          element={getPrivateRoute(<MindfulnessExercise />)}
        />
        <Route
          path="/health/mindfulness-exercise/:id"
          element={getPrivateRoute(<MindfulnessDetails />)}
        />
        <Route path="/health" element={getPrivateRoute(<Health />)} />
        <Route
          path="/longevity-point-history"
          element={getPrivateRoute(<LongevityPointTransaction />)}
        />
        <Route
          path="/my-longevity"
          element={getPrivateRoute(<MyLongevity />)}
        />
        <Route path="/" element={<Navigate to={"/registration"} />} />
        <Route path="*" element={<Navigate to={"/registration"} />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
